import { type } from 'os'
import React, { Dispatch, FC, SetStateAction, memo, useCallback, useEffect, useState } from 'react'
import Select, { SingleValue } from 'react-select'
import { AlertGroup, getSystemAlertsUnreadCount, getSystemAlertsWithParams, SeverityType, SystemAlertModel, updateAllSystemAlertAsReaded, updateSystemAlertStatus } from '../../../api/alertsApi'
import { Guid } from '../../../api/baseApi'
import { useAlertsCountStore } from '../../../hooks/useSystemAlerts'
import { convertUTCDateToLocalDate, formatDate, formatTime } from '../../../services/dateService'
import { Button } from '../Button'
import FormCheckbox from '../formRow/FormCheckbox'
import { FormInput } from '../formRow/FormRow'
import GlobalSpinner from '../globalsSpinner/GlobalSpinner'
import "./SystemAlerts.css"
import Pagination from '../pagination/Pagination'


interface SystemAlertsProps {
}

type SeverityOption = {
    type: SeverityType,
    name: string
}

type AlertGroupOption = {
    type: AlertGroup,
    name: string
}

let severityOptions : SeverityOption[] = [
    {type: SeverityType.Info, name: "Info"},
    {type: SeverityType.Warning, name: "Warning"},
    {type: SeverityType.Important, name: "Important"},
]

const SystemAlerts: FC<SystemAlertsProps> = memo(() => {
    
    let alertGroupOptions : AlertGroupOption[] = [
        {type: AlertGroup.System, name: AlertGroup[AlertGroup.System]},
        {type: AlertGroup.User, name: AlertGroup[AlertGroup.User]},
        {type: AlertGroup.Campaign, name: AlertGroup[AlertGroup.Campaign]},
        {type: AlertGroup.Site, name: AlertGroup[AlertGroup.Site]},
    ];

    let [isLoading, setIsLoading] = useState(false);
    let [alerts, setAlerts] = useState<SystemAlertModel[]>([]);
    let [isShowUnreadedAlerts, setIsShowUnreadedAlerts] = useState(false);
    let [selectedSeverityOption, setSelectedSeverityOption] = useState<SingleValue<SeverityOption>>(severityOptions[0]);
    let [currentPage, setCurrentPage] = useState(1);
    let [totalPages, setTotalPages] = useState(1);

    let [selectedAlertGroupOption, setSelectedAlertGroupOption] = useState<SingleValue<AlertGroupOption>>(null);
    let [searchString, setSearchString] = useState("");

    let [pageLoaded, setPageLoaded] = useState(false)

    let counter = useAlertsCountStore();

    useEffect(() => {
        const fetch = async () => {
            await updateSystemAlerts(currentPage, selectedSeverityOption?.type, isShowUnreadedAlerts, searchString, selectedAlertGroupOption?.type);
            setPageLoaded(true);
        }

        fetch();
    }, [])

    useEffect(()=>{
        let interval = setTimeout(()=> {
            setCurrentPage(1);
            pageLoaded && updateSystemAlerts(1, selectedSeverityOption?.type, isShowUnreadedAlerts, searchString, selectedAlertGroupOption?.type);
        }, 1000)

        return () => {
            clearTimeout(interval)
        }
    },[searchString])

    const updateSystemAlerts = async (currentPage: number, selectedSeverityType: SeverityType = severityOptions[0].type, isShowUnreadedAlerts: boolean, searchString: string, selectedAlertGroup:  AlertGroup | undefined) => {
        setIsLoading(true);
        let alertsResponse = await getSystemAlertsWithParams(currentPage, selectedSeverityType, isShowUnreadedAlerts, searchString, selectedAlertGroup);
        counter.update();
        if (alertsResponse.type == "succes") {
            setAlerts(alertsResponse.data.items);
            setTotalPages(alertsResponse.data.totalPages);
        }
        setIsLoading(false);
    }

    const updateAllSystemAlertStatus = async () => {
        await updateAllSystemAlertAsReaded({})
        await updateSystemAlerts(currentPage, selectedSeverityOption?.type, isShowUnreadedAlerts, searchString, selectedAlertGroupOption?.type);
        setIsShowUnreadedAlerts(false);
    }

    const renderSeverity = (severity: SeverityType) => {
        switch (severity) {
            case SeverityType.Info: return <span>Info</span>
            case SeverityType.Warning: return <span>Warning</span>
            case SeverityType.Important: return <span>Important</span>
        }
    }

    const renderAlertRow = (alerts: SystemAlertModel[]) => {
        let currentAlerts = isShowUnreadedAlerts ? alerts.filter(it => it.isRead === false) : alerts;
        return currentAlerts.map(it => {
            return <tr key={it.id} className={`${!it.isRead ? 'not-read' : ''}`} onClick={() => { !it.isRead && updateAlertStatus(it.id) }}>
                <td>{renderSeverity(it.severity)}</td>
                <td>{AlertGroup[it.group]}</td>
                <td>{convertUTCDateToLocalDate(it.dateTime)} </td>
                <td>{it.message}</td>
                <td></td>
            </tr>
        })
    }

    const updateAlertStatus = async (id: Guid) => {
        await updateSystemAlertStatus(id, {});
        await updateSystemAlerts(currentPage, selectedSeverityOption?.type, isShowUnreadedAlerts, searchString, selectedAlertGroupOption?.type);
    }

    const onSelectedSeverity = async (newValue: SingleValue<SeverityOption>) => {
        setSelectedSeverityOption(newValue);
        setCurrentPage(1);
        await updateSystemAlerts(1, newValue?.type, isShowUnreadedAlerts, searchString, selectedAlertGroupOption?.type);
    }

    const onSelectedAlertGroup = async (newValue: SingleValue<AlertGroupOption>) => {
        setCurrentPage(1);
        setSelectedAlertGroupOption(newValue);
        await updateSystemAlerts(1, selectedSeverityOption?.type, isShowUnreadedAlerts, searchString, newValue?.type);
    }

    const onShowUnreadedChange = async (isShow:boolean) => {
        setCurrentPage(1);
        setIsShowUnreadedAlerts(isShow);
        await updateSystemAlerts(1, selectedSeverityOption?.type, isShow, searchString, selectedAlertGroupOption?.type);
    }

    const getPaginationItems = async (page: number) => {
        setCurrentPage(page);
        await updateSystemAlerts(page, selectedSeverityOption?.type, isShowUnreadedAlerts, searchString, selectedAlertGroupOption?.type);
    }

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-20 w-100">
                <h2>Alerts</h2>

                <div className="alerts-control-row">
                    <div className="d-flex align-items-center">
                        <FormInput 
                            errorMessage = ""
                            id = "alert_search"
                            isError = {false}
                            title = "Search &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;"
                            onChangeValue = {(v) => {setSearchString(v);}}
                            value = {searchString}
                        />
                    </div>
                    <div className="d-flex" style={{ 'width': '300px' }}>
                        <label>Alert Group </label>
                        <Select
                            className={`w-100`}
                            classNamePrefix={`select`}
                            options={alertGroupOptions}
                            filterOption={(option, input) => option.label.toLowerCase().includes(input.toLowerCase())}
                            getOptionLabel={(option) => `${option.name}`}
                            getOptionValue={(option) => `${option.type}`}
                            onChange={onSelectedAlertGroup}
                            value={selectedAlertGroupOption}
                            isMulti={false}
                            isClearable={true}
                        />
                    </div>
                    <div className="d-flex" style={{ 'width': '300px' }}>
                        <label>Minimum Severity </label>
                        <Select
                            className={`w-100`}
                            classNamePrefix={`select`}
                            options={severityOptions}
                            filterOption={(option, input) => option.label.toLowerCase().includes(input.toLowerCase())}
                            getOptionLabel={(option) => `${option.name}`}
                            getOptionValue={(option) => `${option.type}`}
                            onChange={onSelectedSeverity}
                            value={selectedSeverityOption}
                            isMulti={false}
                        />
                    </div>
                    <div className="status-container">
                        <span className="status-block">
                            {isShowUnreadedAlerts ? <span className="current-status online">Show Unread Alerts </span> : <span className="current-status">Show Unread Alerts </span>}
                        </span>&nbsp;
                        <FormCheckbox id="readed-status" checked={isShowUnreadedAlerts} onChange={onShowUnreadedChange} />
                    </div>
                    <Button label='Read All' onClick={updateAllSystemAlertStatus} color='transparent' />
                </div>

            </div>

            {alerts.length ? <> <table className="table table-alerts">
                <thead>
                    <tr>
                        <td></td>
                        <td>Group</td>
                        <td>Date</td>
                        <td>Message</td>
                        <td></td>
                    </tr>
                </thead>
                <tbody>
                    {renderAlertRow(alerts)}
                </tbody>
            </table>
                <Pagination activePage={currentPage} pagesCount={totalPages} changePaginationPage={getPaginationItems} />
            </> : <p>There are no Alerts</p>}

            <GlobalSpinner isShow={isLoading} />
        </>
    )
})

export default SystemAlerts